import React from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

/**
 * UserAvatar — reusable avatar that shows profile_photo_thumb if present
 * and falls back to initials. Used across dashboard, seller profile,
 * listing cards, comments, etc.
 *
 * IMPORTANT: The `photo` prop should be the API path (starts with `/api/...`).
 * The component resolves it against the backend URL from env.
 *
 * Props:
 *  - name: full name (used for initials fallback)
 *  - photo: profile_photo or profile_photo_thumb path
 *  - updatedAt: ISO string for cache-busting when user replaces photo
 *  - size: number in px (default 40)
 *  - className: extra classes
 */
export const UserAvatar = ({ name, photo, updatedAt, size = 40, className, ...rest }) => {
  const initials = (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase() || '')
    .join('') || '?';

  const backendBase = process.env.REACT_APP_BACKEND_URL || '';
  const src = photo ? `${backendBase}${photo}${updatedAt ? `?v=${encodeURIComponent(updatedAt)}` : ''}` : undefined;

  const dim = { width: `${size}px`, height: `${size}px`, fontSize: `${Math.max(10, Math.round(size / 3))}px` };

  return (
    <Avatar className={cn('shrink-0', className)} style={dim} {...rest}>
      {src && <AvatarImage src={src} alt={name || 'User avatar'} />}
      <AvatarFallback className='font-semibold bg-secondary text-secondary-foreground uppercase' style={{ fontSize: dim.fontSize }}>
        {initials}
      </AvatarFallback>
    </Avatar>
  );
};

export default UserAvatar;
